/**
 * Lift2Fit — the scheduled-block banner and the coach's ready block.
 *
 * A block with a future `starts_on` is SAVED, not started. Nothing changes on a
 * read (0041): the page says when it starts and offers the explicit action, so a
 * copy can never be promoted by accident. Several schedules name the earliest;
 * the rest wait in All Programmes.
 *
 * A coach's block is a PRESCRIPTION, not a swap: even a "start now" block lands
 * inactive and never moves the lifter's active slot by itself. The lifter is told
 * it is ready here — with an explicit Activate and a "Not yet" — and the block
 * waits in All Programmes as "new from <date> · from your coach".
 */
import { useState } from 'react';
import type { PlanHistoryEntry } from '@/lib/api';
import { formatDate, pluralize } from '@/lib/format';
import { SparkIcon, TimerIcon } from '@/lib/icons';
import { utcToday } from './new-block';

/** The earliest block saved for a future start, or null. */
export function earliestScheduledBlock(
  blocks: readonly PlanHistoryEntry[],
  today: string = utcToday(),
): PlanHistoryEntry | null {
  return (
    blocks
      .filter((block) => !block.isActive && block.startsOn && block.startsOn > today)
      .sort((a, b) => (a.startsOn! < b.startsOn! ? -1 : a.startsOn! > b.startsOn! ? 1 : 0))[0] ?? null
  );
}

/**
 * The newest coach block waiting for the lifter: inactive (so not the running
 * block), not archived (so not history), coach-owned. Null when there is none —
 * that absence is what hides the ready banner.
 */
export function latestReadyCoachBlock(
  blocks: readonly PlanHistoryEntry[],
): PlanHistoryEntry | null {
  return (
    blocks
      .filter((block) => block.coachOwned === true && !block.isActive && !block.archivedAt)
      .sort((a, b) => ((a.createdAt ?? '') < (b.createdAt ?? '') ? 1 : -1))[0] ?? null
  );
}

export function CoachBlockReadyBanner({
  blocks,
  busy = false,
  onActivate,
}: {
  blocks: readonly PlanHistoryEntry[];
  busy?: boolean;
  onActivate: (entry: PlanHistoryEntry) => void;
}) {
  // "Not yet" dismisses THIS block for this visit; a newer one still appears.
  const [notYet, setNotYet] = useState<string | null>(null);
  const ready = latestReadyCoachBlock(blocks);
  if (!ready || notYet === ready.planId) return null;

  return (
    <div
      data-testid="coach-ready-banner"
      className="flex flex-wrap items-center gap-3 border border-hairline border-l-2 border-l-accent bg-page-subtle px-3 py-2.5 text-[12px] text-ink"
    >
      <SparkIcon className="shrink-0 text-[15px] text-accent" />
      <span className="min-w-0 flex-1">
        <span className="font-bold">Your coach has a new block for you — start it when you’re ready.</span>{' '}
        <span className="font-bold" data-testid="coach-ready-name">{ready.name}</span>
        {ready.startsOn ? <> · new from {formatDate(ready.startsOn)}</> : null}. Starting it replaces the block
        you are running now; that one stays in All Programmes.
      </span>
      <button
        type="button"
        data-testid="coach-ready-activate"
        disabled={busy}
        onClick={() => onActivate(ready)}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
      >
        <SparkIcon className="text-[12px]" /> Activate
      </button>
      <button
        type="button"
        data-testid="coach-ready-not-yet"
        onClick={() => setNotYet(ready.planId)}
        className="inline-flex h-9 shrink-0 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
      >
        Not yet
      </button>
    </div>
  );
}

export function ScheduledBlockBanner({
  blocks,
  audience,
  busy = false,
  onActivate,
}: {
  blocks: readonly PlanHistoryEntry[];
  audience: 'lifter' | 'coach';
  busy?: boolean;
  onActivate: (entry: PlanHistoryEntry) => void;
}) {
  const today = utcToday();
  const scheduled = blocks.filter((block) => !block.isActive && block.startsOn && block.startsOn > today);
  const earliest = earliestScheduledBlock(blocks, today);
  if (!earliest) return null;

  const byCoach = earliest.coachOwned === true;
  const who =
    audience === 'lifter'
      ? byCoach
        ? 'Scheduled by your coach'
        : 'Scheduled by you'
      : byCoach
        ? 'Scheduled by the coach'
        : 'Scheduled by the lifter';

  return (
    <div
      data-testid="scheduled-banner"
      className="flex flex-wrap items-center gap-3 border border-hairline border-l-2 border-l-accent bg-page-subtle px-3 py-2.5 text-[12px] text-ink"
    >
      <TimerIcon className="shrink-0 text-[15px] text-accent" />
      <span className="min-w-0 flex-1">
        <span className="font-bold">Scheduled programme — starts {formatDate(earliest.startsOn!)}.</span>{' '}
        <span className="font-bold">{earliest.name}</span>. {who}.
        {scheduled.length > 1
          ? ` ${pluralize(scheduled.length - 1, 'more block')} ${scheduled.length === 2 ? 'is' : 'are'} in All Programmes.`
          : ''}
      </span>
      {audience === 'coach' || earliest.coachOwned !== true ? (
        <button
          type="button"
          data-testid="scheduled-activate"
          disabled={busy}
          onClick={() => onActivate(earliest)}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <TimerIcon className="text-[12px]" /> Activate it now
        </button>
      ) : (
        <span
          data-testid="scheduled-coach-starts"
          className="inline-flex h-9 shrink-0 items-center border border-hairline px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted"
        >
          Your coach starts this
        </span>
      )}
    </div>
  );
}
